import { useMemo } from 'react';
import { useCart } from '@/context/CartContext';
import { useCatalog } from '@/context/CatalogContext';
import {
  basePrice,
  calcTotals,
  cartRequiresDelivery,
  languageCharges,
  unitPrice,
} from '@/utils/pricing';

/**
 * Joins cart lines against the catalog and splits them into one cart per currency.
 *
 * Publishers price their own books, and the catalogue really is mixed — there are
 * INR titles and USD titles on sale at the same time. Those amounts cannot be
 * added together: there is no exchange rate here, and inventing one would
 * misprice a real charge. So there is no such thing as "the cart total". What
 * there is, is a cart per currency, each with its own subtotal, each checked out
 * on its own.
 *
 * `currency` narrows to a single group — that is what the checkout page passes,
 * so it only ever works with amounts it can legitimately sum.
 *
 * Each line prices independently: two lines for the same book with different
 * translations cost different amounts, so `price` is recomputed per line rather
 * than taken from the product. Lines whose product vanished from the catalog
 * come back under `unavailable` instead of crashing — or, worse, disappearing.
 */
export function useCartItems({ currency = null, discount = 0 } = {}) {
  const { lines } = useCart();
  const { getProduct, status } = useCatalog();

  return useMemo(() => {
    // A line whose book is no longer in the catalog — unpublished since it was
    // added, or saved against a different catalog entirely. It used to be
    // dropped silently, which left the header counting a book the drawer never
    // showed and nothing could delete. It is reported instead, so it can be
    // seen and removed.
    const all = [];
    const unresolved = [];

    for (const line of lines) {
      const product = getProduct(line.id);
      if (!product) {
        unresolved.push(line);
        continue;
      }

      const languages = line.languages ?? [];
      const price = unitPrice(product, languages);

      all.push({
        ...product,
        key: line.key,
        languages,
        price,
        // What that price is made of, so every screen showing the line can say
        // where it came from instead of quoting one opaque number.
        basePrice: basePrice(product),
        languageCharges: languageCharges(product, languages),
        qty: line.qty,
        lineTotal: price * line.qty,
      });
    }

    // Insertion-ordered, so the groups don't reshuffle as the cart changes.
    const byCurrency = new Map();
    for (const item of all) {
      const code = item.currency;
      if (!byCurrency.has(code)) byCurrency.set(code, []);
      byCurrency.get(code).push(item);
    }

    const groups = [...byCurrency].map(([code, groupItems]) => ({
      currency: code,
      items: groupItems,
      requiresDelivery: cartRequiresDelivery(groupItems),
      // The coupon only applies to the cart being checked out; the others are
      // priced plainly until it's their turn.
      totals: calcTotals({ items: groupItems, discount: code === currency ? discount : 0 }),
    }));

    const selected = currency ? groups.find((g) => g.currency === currency) : null;
    const items = selected ? selected.items : all;

    // While the catalog is still loading, every line looks unresolvable. Calling
    // those unavailable would flash "your books are gone" on every page load, so
    // nothing is judged until the catalog has actually answered.
    const isCatalogReady = status === 'ready';

    return {
      /** Every line, across all currencies — for counts and the drawer listing. */
      allItems: all,
      /** Lines the catalog has no product for. Empty until the catalog loads. */
      unavailable: isCatalogReady ? unresolved : [],
      isCatalogReady,
      /** One entry per currency: {currency, items, requiresDelivery, totals}. */
      groups,
      /** The single-currency cart in play, when `currency` was given. */
      items,
      currency: selected?.currency ?? groups[0]?.currency ?? null,
      requiresDelivery: selected
        ? selected.requiresDelivery
        : cartRequiresDelivery(all),
      totals: selected?.totals ?? calcTotals({ items: all, discount }),
      /** True when the cart spans currencies that must be checked out separately. */
      isMultiCurrency: groups.length > 1,
    };
  }, [lines, getProduct, status, currency, discount]);
}
